const US_STATES = new Set(("AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY AS GU MP PR VI AA AE AP FM MH PW").split(" "));
const US_COUNTRIES = new Set(["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA", "U.S.", "U.S.A.", "美国"]);
const text = value => String(value ?? "").trim();

/** Local PDF review checks only; no claim of USPS/carrier deliverability. */
export function getPoAddressIssues(delivery = {}) {
  const issues = [];
  const add = (code, field, message) => issues.push({ code, field, message });
  const street = text(delivery.address1);
  const city = text(delivery.city);
  const state = text(delivery.state).toUpperCase();
  const zip = text(delivery.zip);
  const country = text(delivery.country).toUpperCase();
  const isUS = US_COUNTRIES.has(country);

  if (!street) add("STREET_MISSING", "address1", "Street address is missing. Check the SHIP TO section of the PDF.");
  if (!city) add("CITY_MISSING", "city", "City is missing.");
  if (!zip) add("ZIP_MISSING", "zip", "Postal / ZIP code is missing.");
  if (!country) add("COUNTRY_MISSING", "country", "Country is missing. Confirm the destination country before shipping.");

  // Never impose US state / ZIP rules on Canada or other destinations.
  if (isUS) {
    if (!state) add("STATE_MISSING", "state", "US state is missing.");
    else if (!US_STATES.has(state)) add("STATE_INVALID", "state", "State must be a recognized US two-letter state / territory code. Verify the original PDF; for example, Indiana is IN.");
    if (zip && !/^\d{5}(?:-\d{4})?$/.test(zip)) {
      add("ZIP_INVALID", "zip", "ZIP code must contain 5 digits or use ZIP+4 format (12345-6789). Preserve any leading zero; do not guess missing digits.");
    }
    const specialAddress = /\bP\.?\s*O\.?\s+BOX\s+\d|\b(?:RR|HC)\s+\d|\b(?:PSC|UNIT|CMR)\s+\d|\bGENERAL DELIVERY\b/i.test(street);
    if (street && !specialAddress && (!/\d/.test(street) || !/[a-z]/i.test(street))) {
      add("STREET_INCOMPLETE", "address1", "Street address may be incomplete: a street delivery address normally needs both a building number and a street name. Verify the original SHIP TO address.");
    }
    if (/\bP\.?\s*O\.?\s+BOX\b/i.test(`${street} ${text(delivery.address2)}`)) {
      add("PO_BOX_SERVICE", "address1", "This is a PO Box address. Confirm that the selected carrier service can deliver to PO Boxes before creating a shipment.");
    }
  }

  const structured = `${street} ${text(delivery.address2)}`.toUpperCase();
  const raw = text(delivery.rawShipTo).toUpperCase();
  // Flag only a concrete unit value present in the source but absent from fields.
  // Do not invent a building/room for every hospital or treat the PO number as one.
  const units = [...raw.matchAll(/\b(BLDG|BUILDING|RM|ROOM|STE|SUITE|APT|APARTMENT|UNIT|FL|FLOOR)\.?\s*[:#-]?\s*([A-Z0-9]+(?:-[A-Z0-9]+)?)\b/g)];
  const missing = [...new Set(units.filter(match => {
    const tokens = structured.match(/[A-Z0-9]+(?:-[A-Z0-9]+)?/g) || [];
    return !tokens.includes(match[2]);
  }).map(match => `${match[1]} ${match[2]}`))];
  if (missing.length) add("SECONDARY_ADDRESS_LOST", "address2", `The original SHIP TO includes ${missing.join(", ")}, but these details may be missing from the street / unit fields. Verify building, room and suite information.`);
  return issues;
}
